import { useMemo } from 'react';

import { useChartListQuery } from '@/features/(charts)/chart-list/hooks/use-chart-list-query';
import { ChartGraphIconMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import useFlattenDashboardListQuery from '@/features/Dashboard/hooks/useFlattenDashboardListQuery';
import useFlattenTaxonomyTopicListQuery from '@/features/Taxonomy/hooks/useFlattenTaxonomyTopicListQuery';

import { NAVIGATOR_TARGET, type NavigatorSelectItem } from '../types/navigatorTypes';
import useNavigateSiteMap from './useNavigateSiteMap';

const useNavigatorQueryData = (input: string) => {
  const inputLower = input.toLocaleLowerCase();
  const chartListQuery = useChartListQuery();
  const dashboardListQuery = useFlattenDashboardListQuery();
  const topicListQuery = useFlattenTaxonomyTopicListQuery();
  const siteMapQuery = useNavigateSiteMap();

  const isLoading = useMemo(() => {
    return (
      chartListQuery.isLoading ||
      dashboardListQuery.isLoading ||
      topicListQuery.isLoading
    );
  }, [
    chartListQuery.isLoading,
    dashboardListQuery.isLoading,
    topicListQuery.isLoading,
  ]);

  const result = useMemo(() => {
    const chartList: NavigatorSelectItem[] =
      chartListQuery.data?.map((chart) => {
        const Icon = ChartGraphIconMap.get(chart.graph);
        return {
          target: NAVIGATOR_TARGET.CHART,
          alias: chart.alias,
          name: chart.name,
          icon: Icon ? <Icon style={{ fontSize: 20 }} /> : null,
          href: `/charts/${chart.alias}`,
        };
      }) || [];

    const topicList = topicListQuery.data || [];
    const dashboardList = dashboardListQuery.data || [];

    return [
      ...topicList,
      ...chartList,
      ...dashboardList,
      ...siteMapQuery,
    ].filter(
      (item) =>
        item.name.toLocaleLowerCase().includes(inputLower) ||
        item.path?.toLocaleLowerCase().includes(inputLower),
    );
  }, [
    chartListQuery.data,
    dashboardListQuery.data,
    topicListQuery.data,
    inputLower,
    siteMapQuery,
  ]);

  return {
    isLoading,
    data: result,
  };
};

export default useNavigatorQueryData;
